import { useCallback, useEffect, useMemo, useState } from 'react';

export function useBulkSelection<T>(items: T[], getId: (item: T) => string): {
  selectedIds: string[];
  selectedCount: number;
  allSelected: boolean;
  someSelected: boolean;
  isSelected: (id: string) => boolean;
  toggleSelect: (id: string) => void;
  toggleSelectAll: () => void;
  handleSelectAll: (isSelected: boolean) => void;
  handleSelectOne: (id: string, isSelected: boolean) => void;
  deselectAll: () => void;
} {
  const itemIds = useMemo(() => items.map((item) => getId(item)), [items, getId]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Remove selections that are no longer visible/items changed
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => itemIds.includes(id)));
  }, [itemIds]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id]
    );
  }, []);

  const handleSelectOne = useCallback((id: string, isSelected: boolean) => {
    setSelectedIds((prev) => {
      if (isSelected) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((existingId) => existingId !== id);
    });
  }, []);

  const handleSelectAll = useCallback(
    (isSelected: boolean) => {
      setSelectedIds(isSelected ? itemIds : []);
    },
    [itemIds]
  );

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.length === itemIds.length ? [] : itemIds));
  }, [itemIds]);

  const deselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds]);

  const selectedCount = selectedIds.length;
  const allSelected = selectedCount > 0 && selectedCount === itemIds.length;
  const someSelected = selectedCount > 0 && selectedCount < itemIds.length;

  return {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    isSelected,
    toggleSelect,
    toggleSelectAll,
    handleSelectAll,
    handleSelectOne,
    deselectAll,
  };
}
